import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { formatIsraelDate, formatIsraelTime } from "../lib/time";
import { L, t } from "../lib/translations";
import {
  Send, CheckCircle, ImagePlus, X, ChevronLeft, ChevronRight,
  Clock, History, Sparkles, MessageCircle, Plus, ArrowRight,
  Package, CreditCard, User, Wrench, HelpCircle, Zap,
} from "lucide-react";

const API     = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ── MsgText: parses [label](url) into clickable links ───────────────────── */
function MsgText({ text, lang, onClose }) {
  const navigate = useNavigate();
  if (!text || typeof text !== "string") return <>{text}</>;
  const PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0, match;
  while ((match = PATTERN.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const label = match[1], rawUrl = match[2].trim();
    const isInternal = rawUrl.startsWith("/") || /ifollowers\.site/.test(rawUrl);
    const path = isInternal ? rawUrl.replace(/https?:\/\/ifollowers\.site/, "") || "/" : rawUrl;
    const href = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
    parts.push(
      <a key={match.index} href={isInternal ? path : href}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClose?.();
          if (isInternal) {
            navigate(path);
          } else {
            window.open(href, "_blank", "noopener noreferrer");
          }
        }}
        style={{ color: "#a78bfa", textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}>
        {label}
      </a>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts.map((p, i) => typeof p === "string" ? <span key={i}>{p}</span> : p)}</>;
}

/* ── Image compressor ────────────────────────────────────────────────────── */
const compressImage = (file, maxPx = 1280, quality = 0.82) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const s = Math.min(1, maxPx / Math.max(img.width, img.height, 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * s);
      canvas.height = Math.round(img.height * s);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
        "image/jpeg", quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

/* ── Status helpers ──────────────────────────────────────────────────────── */
const STATUS = {
  open:        { label: { en: "Open",        ru: "Открыт",   he: "פתוח"   }, color: "#facc15", bg: "rgba(250,204,21,0.12)" },
  in_progress: { label: { en: "In Progress", ru: "В работе", he: "בטיפול" }, color: "#60a5fa", bg: "rgba(96,165,250,0.12)" },
  closed:      { label: { en: "Closed",      ru: "Закрыт",   he: "סגור"   }, color: "#4ade80", bg: "rgba(74,222,128,0.12)" },
};
const sc = (s) => (STATUS[s] || STATUS.open).color;
const sb = (s) => (STATUS[s] || STATUS.open).bg;
const sl = (s, lang) => { const st = STATUS[s] || STATUS.open; return st.label[lang] || st.label.en; };

function relTime(iso, lang) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff/60000), h = Math.floor(diff/3600000), d = Math.floor(diff/86400000);
  if (m < 2)  return t("Admin.now", lang);
  if (m < 60) return L(lang, `${m}m`, `${m}м`, `${m} דק'`);
  if (h < 24) return L(lang, `${h}h`, `${h}ч`, `${h} ש'`);
  if (d < 7)  return L(lang, `${d}d`, `${d}дн`, `${d} י'`);
  return formatIsraelDate(iso);
}

const resolveUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

/* ── Bot flow data ───────────────────────────────────────────────────────── */
const BOT_TOPICS = [
  {
    id: "order", Icon: Package,
    color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)",
    label: { en: "Order issue",      ru: "Проблема с заказом",   he: "בעיית הזמנה"   },
    desc:  { en: "Delivery, refund…", ru: "Доставка, возврат…",  he: "משלוח, החזר…"  },
  },
  {
    id: "payment", Icon: CreditCard,
    color: "#3b82f6", bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.25)",
    label: { en: "Payment / Top-up",  ru: "Оплата / Пополнение", he: "תשלום / טעינה"  },
    desc:  { en: "Balance, billing…", ru: "Баланс, оплата…",     he: "יתרה, חיוב…"   },
  },
  {
    id: "account", Icon: User,
    color: "#8b5cf6", bg: "rgba(139,92,246,0.12)", border: "rgba(139,92,246,0.25)",
    label: { en: "Account",           ru: "Аккаунт",             he: "חשבון"          },
    desc:  { en: "Login, data…",      ru: "Вход, данные…",       he: "כניסה, מידע…"  },
  },
  {
    id: "technical", Icon: Wrench,
    color: "#ec4899", bg: "rgba(236,72,153,0.12)", border: "rgba(236,72,153,0.25)",
    label: { en: "Technical issue",   ru: "Техническая проблема",he: "בעיה טכנית"     },
    desc:  { en: "Errors, bugs…",     ru: "Ошибки, баги…",       he: "שגיאות, באגים…"},
  },
  {
    id: "other", Icon: HelpCircle,
    color: "#6b7280", bg: "rgba(107,114,128,0.12)", border: "rgba(107,114,128,0.25)",
    label: { en: "Other",             ru: "Другое",              he: "אחר"            },
    desc:  { en: "Something else",    ru: "Другой вопрос",       he: "שאלה אחרת"     },
  },
];

const BOT_DETAILS = {
  order: {
    q: { en: "What's the issue with your order?", ru: "В чём проблема с заказом?", he: "מה הבעיה עם ההזמנה?" },
    opts: [
      { id: "not_delivered", emoji: "📭", label: { en: "Not delivered",    ru: "Не доставлен",       he: "לא נמסר"       } },
      { id: "partial",       emoji: "📦", label: { en: "Partial delivery", ru: "Частичная доставка", he: "מסירה חלקית"   } },
      { id: "wrong",         emoji: "❌", label: { en: "Wrong order",      ru: "Неверный заказ",     he: "הזמנה שגויה"   } },
      { id: "refund",        emoji: "💸", label: { en: "Need a refund",    ru: "Нужен возврат",      he: "אני רוצה החזר" } },
    ],
  },
  payment: {
    q: { en: "What's the payment issue?", ru: "В чём проблема с оплатой?", he: "מה הבעיה עם התשלום?" },
    opts: [
      { id: "not_credited",  emoji: "💰", label: { en: "Payment not credited",   ru: "Деньги не зачислены",        he: "התשלום לא זוכה"  } },
      { id: "cant_pay",      emoji: "🚫", label: { en: "Can't complete payment", ru: "Не могу оплатить",           he: "לא מצליח לשלם"   } },
      { id: "topup_missing", emoji: "❓", label: { en: "Top-up not showing",     ru: "Пополнение не отобразилось", he: "הטעינה לא מופיעה"} },
      { id: "refund",        emoji: "💸", label: { en: "Need a refund",          ru: "Нужен возврат",              he: "אני רוצה החזר"   } },
    ],
  },
  account: {
    q: { en: "What's the account issue?", ru: "В чём проблема с аккаунтом?", he: "מה הבעיה עם החשבון?" },
    opts: [
      { id: "cant_login", emoji: "🔒", label: { en: "Can't log in",           ru: "Не могу войти",          he: "לא מצליח להתחבר"} },
      { id: "banned",     emoji: "⛔", label: { en: "Account blocked",        ru: "Аккаунт заблокирован",   he: "חשבון חסום"     } },
      { id: "points",     emoji: "⭐", label: { en: "Missing points/rewards", ru: "Пропали баллы",          he: "נקודות חסרות"   } },
      { id: "data",       emoji: "📋", label: { en: "Wrong data shown",       ru: "Неверные данные",         he: "מידע שגוי"      } },
    ],
  },
  technical: {
    q: { en: "What's the technical issue?", ru: "В чём техническая проблема?", he: "מה הבעיה הטכנית?" },
    opts: [
      { id: "crash",   emoji: "💥", label: { en: "Error / crash",       ru: "Ошибка / вылет",      he: "שגיאה / קריסה" } },
      { id: "slow",    emoji: "🐌", label: { en: "Site is slow",         ru: "Сайт медленный",      he: "האתר איטי"     } },
      { id: "feature", emoji: "🔧", label: { en: "Feature not working", ru: "Функция не работает", he: "תכונה לא עובדת"} },
      { id: "display", emoji: "🖥️", label: { en: "Display bug",         ru: "Визуальный баг",      he: "באג תצוגה"     } },
    ],
  },
  other: null,
};

const AI_CHIPS = [
  { en: "How do I order?",        ru: "Как сделать заказ?",    he: "איך מזמינים?" },
  { en: "How to top up?",         ru: "Как пополнить баланс?", he: "איך טוענים?" },
  { en: "Free followers & likes", ru: "Бесплатные подарки",    he: "חינם עוקבים" },
  { en: "Order not delivered",    ru: "Заказ не пришёл",       he: "ההזמנה לא הגיעה" },
  { en: "Referral program",       ru: "Реферальная программа", he: "תוכנית הפניות" },
  { en: "What are iPoints?",      ru: "Что такое iPoints?",    he: "מה זה iPoints?" },
];

/* ── Cool support icon ───────────────────────────────────────────────────── */
function SupportIcon({ size = 36 }) {
  return (
    <div style={{ width: size, height: size, position: "relative", flexShrink: 0 }}>
      <img
        src="/ai-logo.png"
        alt="iFollowers Support"
        style={{
          width: size, height: size,
          borderRadius: "50%",
          objectFit: "cover",
          display: "block",
          boxShadow: "0 2px 10px rgba(139,92,246,0.35)",
        }}
      />
      {/* Online dot */}
      <div style={{
        position: "absolute", bottom: 0, right: 0,
        width: size * 0.28, height: size * 0.28, borderRadius: "50%",
        background: "#22c55e",
        border: "2px solid var(--bg-secondary)",
        boxShadow: "0 0 6px rgba(34,197,94,0.6)",
      }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN WIDGET
═══════════════════════════════════════════════════════════════════════════ */
export default function SupportWidget() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRtl = lang === "he";

  const [open, setOpen]           = useState(false);
  const [lbImg, setLbImg]         = useState("");
  const [view, setView]           = useState("chat"); // "chat" | "history"
  const [tickets, setTickets]     = useState([]);
  const [activeId, setActiveId]   = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyAtts, setReplyAtts] = useState([]);
  const [sending, setSending]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);
  const newConvMode    = useRef(false);
  const sendingRef     = useRef(false);

  const [botTopic, setBotTopic]               = useState(null);
  const [botDetail, setBotDetail]             = useState(null);
  const [botDone, setBotDone]                 = useState(false);
  const [botLinkStep, setBotLinkStep]         = useState(false);
  const [botItems, setBotItems]               = useState([]);
  const [botItemsLoading, setBotItemsLoading] = useState(false);
  const [botLinkedId, setBotLinkedId]         = useState(null);
  const [botLinkedType, setBotLinkedType]     = useState(null);

  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiInput, setAiInput]       = useState("");
  const [wantsHuman, setWantsHuman] = useState(false);
  const aiInputRef   = useRef(null);
  const aiSessionRef = useRef(null);

  /* scroll lock */
  useEffect(() => {
    const isMobile = () => window.innerWidth < 640;
    if (open && isMobile()) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  /* keyboard height on mobile */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const panel = document.querySelector(".sw-panel");
      if (!panel || window.innerWidth >= 640) return;
      const kh = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      panel.style.setProperty("--sw-kb", `${kh}px`);
    };
    vv.addEventListener("resize", update); vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);

  /* open-support event from navbar */
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-support", h);
    return () => window.removeEventListener("open-support", h);
  }, []);

  /* if all tickets closed → new conv mode */
  useEffect(() => {
    if (!open) return;
    if (tickets.length > 0 && tickets.every(t => t.status === "closed") && !newConvMode.current) {
      newConvMode.current = true; setActiveId("");
    }
  }, [open, tickets]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("support-open-state", { detail: { open } }));
  }, [open]);

  /* fetch tickets */
  const fetchTickets = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API}/tickets`, { credentials: "include" });
      if (!r.ok) return;
      const data = await r.json();
      setTickets(data);
      setActiveId(cur => {
        if (newConvMode.current) return "";
        if (data.some(t => t.ticket_id === cur)) return cur;
        const active = data.find(t => t.status !== "closed");
        return active?.ticket_id || data[0]?.ticket_id || "";
      });
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchTickets();
    const iv = setInterval(fetchTickets, 12000);
    return () => clearInterval(iv);
  }, [fetchTickets, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeId, open, tickets]);

  /* auto-focus reply textarea when human-ready mode activates */
  useEffect(() => {
    if (wantsHuman && botDone && !activeId) {
      setTimeout(() => textareaRef.current?.focus(), 150);
    }
  }, [wantsHuman, botDone, activeId]);

  const activeTicket = useMemo(() => tickets.find(t => t.ticket_id === activeId) || null, [tickets, activeId]);
  const totalUnread  = tickets.reduce((s, t) => s + (t.user_unread_count || 0), 0);
  const isClosed     = activeTicket?.status === "closed";

  /* mark active ticket read when widget is open and chat is visible */
  const activeUnread = activeTicket?.user_unread_count || 0;
  useEffect(() => {
    if (!open || view !== "chat" || !activeId || !activeUnread) return;
    markRead(activeId);
  }, [open, activeId, view, activeUnread]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Actions ── */
  const markRead = async (tid) => {
    await fetch(`${API}/tickets/${tid}/read`, { method: "PUT", credentials: "include" });
    setTickets(cur => cur.map(t => t.ticket_id === tid ? { ...t, user_unread_count: 0 } : t));
  };

  const sendReply = async () => {
    if (sendingRef.current) return;
    const text = replyText.trim();
    if (!text && replyAtts.length === 0) return;
    sendingRef.current = true; setSending(true);
    try {
      if (!activeId) {
        const topicData  = BOT_TOPICS.find(t => t.id === botTopic);
        const detailData = botDetail && BOT_DETAILS[botTopic]?.opts?.find(o => o.id === botDetail);
        const subjectLabel = detailData
          ? (detailData.label[lang] || detailData.label.en)
          : topicData ? (topicData.label[lang] || topicData.label.en) : "";
        const subject = subjectLabel
          ? (subjectLabel + (text ? " · " + text.slice(0, 40) : ""))
          : (text.slice(0, 60) || t("c.SupportWidget.support_request", lang));
        const botSelectionsPayload = topicData ? {
          topic_id: botTopic, topic_emoji: topicData.Icon?.name || botTopic,
          topic_label: topicData.label, detail_id: botDetail || null,
          detail_label: detailData ? detailData.label : null,
        } : null;
        // Include AI conversation history so admins can see prior Q&A
        const aiConversationPayload = aiMessages.length > 0
          ? aiMessages.map(m => ({ role: m.role, content: m.content }))
          : null;
        const r = await fetch(`${API}/tickets`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ subject, message: text, attachments: replyAtts, bot_selections: botSelectionsPayload, linked_order_id: botLinkedType === "order" ? botLinkedId : null, linked_topup_id: botLinkedType === "topup" ? botLinkedId : null, ai_conversation: aiConversationPayload }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setReplyText(""); setReplyAtts([]);
        newConvMode.current = false;
        await fetchTickets(); setActiveId(d.ticket_id);
      } else {
        const r = await fetch(`${API}/tickets/${activeId}/reply`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ message: text, attachments: replyAtts }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setReplyText(""); setReplyAtts([]);
        await fetchTickets();
      }
    } catch (e) { toast.error(e.message); } finally { setSending(false); sendingRef.current = false; }
  };

  const uploadFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (replyAtts.length + arr.length > 4) { toast.error(t("c.SupportWidget.max_4_images", lang)); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        const compressed = await compressImage(f);
        const fd = new FormData(); fd.append("file", compressed);
        const r = await fetch(`${API}/upload`, { method: "POST", body: fd, credentials: "include" });
        const d = await r.json(); if (!r.ok) throw new Error(d.detail);
        uploaded.push({ url: d.url, filename: d.filename });
      }
      setReplyAtts(c => [...c, ...uploaded]);
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  };

  const startNewConversation = () => {
    newConvMode.current = true;
    setActiveId(""); setReplyText(""); setReplyAtts([]);
    setBotTopic(null); setBotDetail(null); setBotDone(false);
    setBotLinkStep(false); setBotLinkedId(null); setBotLinkedType(null);
    setWantsHuman(false); setAiMessages([]); setAiInput(""); aiSessionRef.current = null;
    setView("chat");
    setTimeout(() => aiInputRef.current?.focus(), 100);
  };

  const saveAiSession = (msgs) => {
    if (!user || !msgs.length) return;
    if (!aiSessionRef.current) aiSessionRef.current = "aic_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    fetch(`${API}/user/ai-conversation`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: aiSessionRef.current, messages: msgs, lang }),
    }).catch(() => {});
  };

  const sendAiMessage = async (overrideText) => {
    const msg = (overrideText !== undefined ? overrideText : aiInput).trim();
    if (!msg || aiLoading) return;
    setAiInput("");
    const next = [...aiMessages, { role: "user", content: msg }];
    setAiMessages(next); setAiLoading(true);
    try {
      const r = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ messages: next, lang }),
      });
      const d = await r.json();
      const replyText = r.ok ? d.reply : t("c.SupportWidget.sorry_try_again", lang);
      const full = [...next, { role: "assistant", content: replyText }];
      setAiMessages(full);
      saveAiSession(full);
    } catch {
      const full = [...next, { role: "assistant", content: t("c.SupportWidget.connection_error", lang) }];
      setAiMessages(full);
    } finally { setAiLoading(false); }
  };

  const openHistoryTicket = async (ticket) => {
    setActiveId(ticket.ticket_id); setView("chat");
    if ((ticket.user_unread_count || 0) > 0) await markRead(ticket.ticket_id);
  };

  if (!user) return null;

  /* ── Derived flags for pre-chat flow ── */
  // Are we in the pre-chat selection screens?
  const inBotSelect = !activeId && wantsHuman && !botDone;
  // Header title
  const headerTitle = view === "history"
    ? t("c.SupportWidget.chat_history", lang)
    : inBotSelect
      ? (botLinkStep
          ? (botTopic === "order" ? t("c.SupportWidget.select_order", lang) : t("c.SupportWidget.select_top_up", lang))
          : botDetail === null && botTopic !== null
            ? t("c.SupportWidget.tell_us_more", lang)
            : t("c.SupportWidget.how_can_we_help", lang))
      : t("Admin.support", lang);

  return (
    <>
      {/* Lightbox */}
      {lbImg && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
          onClick={() => setLbImg("")}>
          <button onClick={() => setLbImg("")}
            className="absolute top-4 end-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "var(--surface-3)", color: "#fff" }}>
            <X className="w-5 h-5" />
          </button>
          <img src={lbImg} alt="" className="max-w-[90vw] max-h-[85vh] rounded-2xl object-contain"
            onClick={e => e.stopPropagation()} />
        </div>
      )}

      <style>{`
        @media(max-width:639px){
          .sw-panel{
            left:0!important;right:0!important;
            top:64px!important;bottom:0!important;
            width:100%!important;
            height:calc(100dvh - 64px - var(--sw-kb,0px))!important;
            max-height:none!important;
            border-radius:0!important;
            border-left:none!important;border-right:none!important;border-bottom:none!important;
          }
          .sw-messages{overscroll-behavior:contain;}
        }
        @media(min-width:640px){
          .sw-panel{
            bottom:16px!important;right:20px!important;
            left:auto!important;top:auto!important;
            width:370px!important;height:590px!important;
            border-radius:24px!important;
          }
          [dir="rtl"] .sw-panel{right:auto!important;left:20px!important;}
        }
        .sw-topic-card{transition:transform .15s,background .15s;}
        .sw-topic-card:hover{transform:translateY(-1px);}
        .sw-topic-card:active{transform:scale(.97);}
        .sw-detail-btn:hover{opacity:.85;}
        .sw-detail-btn:active{transform:scale(.96);}
      `}</style>

      {open && (
        <div className="fixed inset-0 z-40"
          style={{ background: "rgba(0,0,0,0.3)" }}
          onClick={() => setOpen(false)} />
      )}

      {/* ════════════════ PANEL ════════════════════════════════════════════════ */}
      <div className="sw-panel fixed bottom-0 left-0 right-0 z-40 flex flex-col"
        dir={isRtl ? "rtl" : "ltr"}
        style={{
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-color)",
          boxShadow: "0 -8px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.1)",
          overflow: "hidden",
          transition: "transform 0.32s cubic-bezier(.4,0,.2,1), opacity .22s",
          transform: open ? "translateY(0)" : "translateY(100%)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
          style={{ borderBottom: "1px solid var(--border-color)" }}>

          {/* Back button in bot sub-steps */}
          {inBotSelect && (botTopic || botLinkStep) && (
            <button
              onClick={() => {
                if (botLinkStep) { setBotLinkStep(false); setBotDetail(null); return; }
                if (botTopic)   { setBotTopic(null); return; }
              }}
              className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors flex-shrink-0"
              style={{ color: "var(--text-secondary)" }}>
              <ChevronLeft className="w-5 h-5 rtl-flip" />
            </button>
          )}

          <SupportIcon size={34} />

          <div className="flex-1 min-w-0">
            <div className="font-black text-sm font-outfit" style={{ color: "var(--text-primary)" }}>
              {inBotSelect && botLinkStep
                ? (botTopic === "order" ? t("c.SupportWidget.select_order", lang) : t("c.SupportWidget.select_top_up", lang))
                : inBotSelect && botTopic
                  ? t("c.SupportWidget.tell_us_more", lang)
                  : inBotSelect
                    ? t("c.SupportWidget.how_can_we_help", lang)
                    : view === "history"
                      ? t("c.SupportWidget.chat_history", lang)
                      : t("c.SupportWidget.ifollowers_support", lang)}
            </div>
            {view === "chat" && activeTicket ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ background: sb(activeTicket.status), color: sc(activeTicket.status) }}>
                  {sl(activeTicket.status, lang)}
                </span>
                <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                  #{activeTicket.ticket_id.slice(-6).toUpperCase()}
                </span>
              </div>
            ) : view === "chat" && !inBotSelect && wantsHuman && !activeId ? (
              <div className="flex items-center gap-1 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow: "0 0 4px #22c55e" }} />
                <span className="text-[10px] font-semibold" style={{ color: "#22c55e" }}>
                  {L(lang, "Human support — agent will reply soon", "Поддержка — оператор ответит скоро", "תמיכה אנושית — נציג יגיב בקרוב")}
                </span>
              </div>
            ) : view === "chat" && !inBotSelect ? (
              <div className="flex items-center gap-1 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                  {t("c.SupportWidget.online_replies_within_minutes", lang)}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {view === "chat" && !inBotSelect && (
              <button onClick={startNewConversation}
                className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors"
                style={{ color: "var(--text-secondary)" }}
                title={t("c.SupportWidget.new_chat", lang)}>
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors"
              style={{ color: "var(--text-secondary)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Bottom tab bar — always visible ────────────────────────────────── */}
        {!inBotSelect && (
          <div className="flex-shrink-0 flex" style={{ borderBottom: "1px solid var(--border-color)" }}>
            {[
              { id: "chat",    icon: <MessageCircle className="w-4 h-4" />, label: t("c.SupportWidget.chat", lang) },
              { id: "history", icon: <History className="w-4 h-4" />,       label: t("Admin.history", lang) },
            ].map(tab => (
              <button key={tab.id}
                onClick={() => setView(tab.id)}
                className="relative flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors"
                style={{ color: view === tab.id ? "#8B5CF6" : "var(--text-secondary)" }}>
                <span className="relative">
                  {tab.icon}
                  {tab.id === "history" && totalUnread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full text-[8px] font-black text-white flex items-center justify-center"
                      style={{ background: "#E1306C" }}>
                      {totalUnread > 9 ? "9+" : totalUnread}
                    </span>
                  )}
                </span>
                {tab.label}
                {/* Active indicator */}
                {view === tab.id && (
                  <span className="absolute bottom-0 inset-x-6 h-0.5 rounded-full" style={{ background: "#8B5CF6" }} />
                )}
              </button>
            ))}
          </div>
        )}

        {/* ════════════════ CHAT VIEW ════════════════════════════════════════ */}
        {view === "chat" && (
          <>
            {/* ── Pre-chat topic/detail selection (full panel) ── */}
            {inBotSelect ? (
              <div className="flex-1 overflow-y-auto px-4 py-4">

                {/* Step: topic selection */}
                {!botTopic && (
                  <div className="flex flex-col gap-3">
                    <p className="text-xs text-center mb-1" style={{ color: "var(--text-secondary)" }}>
                      {t("c.SupportWidget.choose_a_category_and_we_ll_route_you_to_the_rig", lang)}
                    </p>

                    {/* AI chat shortcut */}
                    <button
                      className="sw-topic-card flex items-center gap-4 px-4 py-3.5 rounded-2xl text-start transition-all"
                      style={{
                        background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(225,48,108,0.10))",
                        border: "1px solid rgba(139,92,246,0.35)",
                      }}
                      onClick={() => { setWantsHuman(false); }}>
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.35)" }}>
                        <Sparkles className="w-5 h-5" style={{ color: "#8B5CF6" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                            {L(lang, "Chat with AI", "Спросить AI", "שאל את ה-AI")}
                          </span>
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                            style={{ background: "#8B5CF6", color: "#fff" }}>
                            {L(lang, "Instant", "Быстро", "מיידי")}
                          </span>
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                          {L(lang, "Answers 24/7 without waiting", "Ответы 24/7 без ожидания", "תשובות 24/7 ללא המתנה")}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 flex-shrink-0 rtl-flip" style={{ color: "#8B5CF6" }} />
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-2 my-1">
                      <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                        {L(lang, "or contact support", "или связаться с поддержкой", "או צור קשר עם תמיכה")}
                      </span>
                      <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {BOT_TOPICS.map(t => {
                        const Icon = t.Icon;
                        return (
                          <button key={t.id}
                            className="sw-topic-card flex items-center gap-4 px-4 py-3.5 rounded-2xl text-start"
                            style={{ background: "var(--surface-1)", border: `1px solid var(--border-color)` }}
                            onClick={() => { setBotTopic(t.id); if (t.id === "other") setBotDone(true); }}>
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                              style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                              <Icon className="w-5 h-5" style={{ color: t.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                                {t.label[lang] || t.label.en}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                                {t.desc[lang] || t.desc.en}
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 flex-shrink-0 rtl-flip" style={{ color: "var(--text-secondary)" }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step: detail selection */}
                {botTopic && BOT_DETAILS[botTopic] && !botDetail && !botLinkStep && (
                  <div className="flex flex-col gap-3">
                    {/* Selected topic badge */}
                    {(() => {
                      const t = BOT_TOPICS.find(x => x.id === botTopic);
                      const Icon = t?.Icon;
                      return t ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-1"
                          style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: "rgba(0,0,0,0.2)" }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                          </div>
                          <span className="text-xs font-bold" style={{ color: t.color }}>
                            {t.label[lang] || t.label.en}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                      {(BOT_DETAILS[botTopic].q)[lang] || (BOT_DETAILS[botTopic].q).en}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOT_DETAILS[botTopic].opts.map(o => (
                        <button key={o.id}
                          className="sw-detail-btn flex flex-col items-start gap-1.5 px-3 py-3 rounded-2xl text-start transition-all"
                          style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}
                          onClick={async () => {
                            setBotDetail(o.id);
                            if (botTopic === "order" || botTopic === "payment") {
                              setBotLinkStep(true); setBotItemsLoading(true);
                              try {
                                const ep = botTopic === "order" ? `${API}/orders` : `${API}/topup/history`;
                                const r = await fetch(ep, { credentials: "include" });
                                if (r.ok) setBotItems(await r.json());
                              } catch {} finally { setBotItemsLoading(false); }
                            } else { setBotDone(true); }
                          }}>
                          <span className="text-xl">{o.emoji}</span>
                          <span className="text-xs font-semibold leading-snug" style={{ color: "var(--text-primary)" }}>
                            {o.label[lang] || o.label.en}
                          </span>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setBotDone(true)}
                      className="text-xs text-center py-1 hover:underline"
                      style={{ color: "var(--text-secondary)" }}>
                      {t("c.SupportWidget.skip_just_type_my_issue", lang)}
                    </button>
                  </div>
                )}

                {/* Step: order/topup picker */}
                {botLinkStep && (
                  <div className="flex flex-col gap-3">
                    {(() => {
                      const t = BOT_TOPICS.find(x => x.id === botTopic);
                      return t ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-1"
                          style={{ background: t.bg, border: `1px solid ${t.border}` }}>
                          <t.Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: t.color }} />
                          <span className="text-xs font-bold" style={{ color: t.color }}>
                            {t.label[lang] || t.label.en}
                          </span>
                          <ChevronRight className="w-3 h-3 opacity-50 rtl-flip" style={{ color: t.color }} />
                          <span className="text-xs font-semibold" style={{ color: t.color }}>
                            {BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.label[lang]}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                      {botTopic === "order"
                        ? t("c.SupportWidget.which_order_is_affected", lang)
                        : t("c.SupportWidget.which_top_up_is_affected", lang)}
                    </p>
                    {botItemsLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : botItems.length === 0 ? (
                      <p className="text-xs text-center py-4" style={{ color: "var(--text-secondary)" }}>
                        {t("c.SupportWidget.no_recent_items_found", lang)}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto">
                        {botItems.slice(0, 8).map(item => {
                          const id  = item.order_id || item.topup_id;
                          const type = item.order_id ? "order" : "topup";
                          const sel  = botLinkedId === id;
                          return (
                            <button key={id}
                              onClick={() => { setBotLinkedId(id); setBotLinkedType(type); setBotLinkStep(false); setBotDone(true); }}
                              className="flex items-center gap-3 px-3.5 py-3 rounded-2xl text-start transition-all"
                              style={{
                                background: sel ? "rgba(139,92,246,0.12)" : "var(--surface-1)",
                                border: `1px solid ${sel ? "rgba(139,92,246,0.3)" : "var(--border-color)"}`,
                              }}>
                              <span className="text-xl flex-shrink-0">{type === "order" ? "📦" : "💳"}</span>
                              <div className="flex-1 min-w-0">
                                {type === "order" ? (
                                  <>
                                    <div className="text-xs font-bold truncate" style={{ color: "var(--text-primary)" }}>
                                      #{item.order_number} — {item.product_name}
                                    </div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                                      {item.status} · {item.quantity} {t("c.SupportWidget.units", lang)}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>
                                      {item.amount} {item.currency || "₪"}
                                    </div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                                      {item.method} · {item.status}
                                    </div>
                                  </>
                                )}
                              </div>
                              {sel && <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-400" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <button onClick={() => { setBotLinkStep(false); setBotDone(true); }}
                      className="text-xs text-center py-1 hover:underline"
                      style={{ color: "var(--text-secondary)" }}>
                      {t("c.SupportWidget.skip_this_step", lang)}
                    </button>
                  </div>
                )}
              </div>

            ) : (
              /* ── Normal chat messages area ── */
              <div className="sw-messages flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-2">

                {/* Welcome / context bubble — changes based on mode */}
                <div className="flex items-end gap-2.5 mb-4">
                  <SupportIcon size={30} />
                  <div className="max-w-[82%]">
                    <div className="text-[10px] font-semibold mb-1"
                      style={{ color: (wantsHuman && !activeId) ? "#22c55e" : "#a78bfa" }}>
                      {t("c.SupportWidget.ifollowers_support_2", lang)}
                    </div>
                    <div className="rounded-2xl rounded-bl-[6px] px-4 py-3 text-[13px] leading-relaxed whitespace-pre-line"
                      style={{
                        background: "var(--surface-2)",
                        border: `1px solid ${(wantsHuman && !activeId) ? "rgba(34,197,94,0.25)" : "var(--border-color)"}`,
                        color: "var(--text-primary)",
                      }}>
                      {wantsHuman && !activeId
                        ? L(lang,
                            "You're now connected to our support team 👋\n\nDescribe your issue below and an agent will respond shortly.",
                            "Вы подключены к нашей поддержке 👋\n\nОпишите проблему — оператор ответит скоро.",
                            "אתה מחובר עכשיו לצוות התמיכה שלנו 👋\n\nתאר את הבעיה למטה ונציג יגיב בהקדם."
                          )
                        : t("c.SupportWidget.welcome_to_ifollowers_support_our_ai_assistant_a", lang)
                      }
                    </div>
                  </div>
                </div>

                {/* Topic context badge — human ready mode only */}
                {wantsHuman && !activeId && botTopic && (
                  <div className="flex justify-center mb-3">
                    {(() => {
                      const tp = BOT_TOPICS.find(x => x.id === botTopic);
                      if (!tp) return null;
                      return (
                        <div className="inline-flex items-center gap-2 flex-wrap px-3 py-2 rounded-2xl"
                          style={{ background: tp.bg, border: `1px solid ${tp.border}` }}>
                          <tp.Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: tp.color }} />
                          <span className="text-xs font-bold" style={{ color: tp.color }}>
                            {tp.label[lang] || tp.label.en}
                          </span>
                          {botDetail && BOT_DETAILS[botTopic] && (
                            <>
                              <ChevronRight className="w-3 h-3 opacity-50 rtl-flip" style={{ color: tp.color }} />
                              <span className="text-xs font-semibold" style={{ color: tp.color }}>
                                {BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.label[lang]
                                  || BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.label.en}
                              </span>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* AI messages — only in AI mode (not when wantsHuman) */}
                {!activeId && !wantsHuman && aiMessages.map((m, i) => {
                  const isUser = m.role === "user";
                  return (
                    <div key={i} className={`flex items-end gap-2.5 mb-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
                      <div className="w-7 h-7 rounded-2xl flex-shrink-0 flex items-center justify-center"
                        style={{ background: isUser ? "linear-gradient(135deg,#E1306C,#8B5CF6)" : "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                        {isUser
                          ? <span className="text-[10px] font-black text-white">{(user?.name?.[0] || "U").toUpperCase()}</span>
                          : <Sparkles className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} max-w-[78%]`}>
                        {!isUser && <span className="text-[10px] font-semibold mb-1" style={{ color: "#a78bfa" }}>AI</span>}
                        <div className="rounded-2xl px-3.5 py-2.5"
                          style={{
                            background: isUser ? "rgba(139,92,246,0.15)" : "var(--surface-2)",
                            border: `1px solid ${isUser ? "rgba(139,92,246,0.25)" : "var(--border-color)"}`,
                            borderBottomRightRadius: isUser ? 6 : 16,
                            borderBottomLeftRadius:  isUser ? 16 : 6,
                          }}>
                          <div className="text-[13px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: "var(--text-primary)" }}>
                            <MsgText text={m.content} lang={lang} onClose={() => setOpen(false)} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* AI loading — only in AI mode */}
                {!activeId && !wantsHuman && aiLoading && (
                  <div className="flex items-end gap-2.5 mb-2.5">
                    <div className="w-7 h-7 rounded-2xl flex-shrink-0 flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="rounded-2xl px-4 py-3"
                      style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
                      <div className="flex gap-1 items-center h-3">
                        {[0,150,300].map(d => (
                          <span key={d} className="w-1.5 h-1.5 rounded-full animate-bounce"
                            style={{ background: "#a78bfa", animationDelay: `${d}ms` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* "Talk to agent" pill — only in AI mode, after first AI reply */}
                {!activeId && !wantsHuman && aiMessages.some(m => m.role === "assistant") && !aiLoading && (
                  <div className="flex justify-center my-3">
                    <button onClick={() => { newConvMode.current = true; setWantsHuman(true); setBotTopic(null); setBotDetail(null); setBotDone(false); }}
                      className="flex items-center gap-2 text-xs px-4 py-2 rounded-full font-semibold transition-all hover:opacity-80"
                      style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                      <MessageCircle className="w-3.5 h-3.5" />
                      {t("c.SupportWidget.talk_to_a_real_agent", lang)}
                    </button>
                  </div>
                )}

                {/* Bot selection tag on existing ticket */}
                {activeTicket?.bot_selections && (
                  <div className="flex justify-center mb-3">
                    <div className="inline-flex items-center gap-2 flex-wrap px-3 py-2 rounded-2xl"
                      style={{ background: "rgba(79,70,229,0.07)", border: "1px solid rgba(79,70,229,0.18)" }}>
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(139,92,246,0.5)" }}>
                        {t("Admin.category", lang)}
                      </span>
                      <span className="text-xs font-bold" style={{ color: "#a78bfa" }}>
                        {activeTicket.bot_selections.topic_label?.[lang] || activeTicket.bot_selections.topic_label?.en}
                      </span>
                      {activeTicket.bot_selections.detail_label && (
                        <>
                          <ChevronRight className="w-3 h-3 opacity-30 rtl-flip" />
                          <span className="text-xs font-semibold" style={{ color: "#a78bfa" }}>
                            {activeTicket.bot_selections.detail_label?.[lang] || activeTicket.bot_selections.detail_label?.en}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Ticket messages */}
                {activeTicket?.messages?.map((msg, i) => {
                  const isUser = msg.sender_type === "user";
                  return (
                    <div key={i} className={`flex items-end gap-2.5 mb-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
                      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden"
                        style={{ background: isUser ? "linear-gradient(135deg,#E1306C,#8B5CF6)" : "transparent" }}>
                        {isUser
                          ? <span className="text-[10px] font-black text-white">{(user?.name?.[0] || "U").toUpperCase()}</span>
                          : <img src="/ai-logo.png" alt="AI" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />}
                      </div>
                      <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} max-w-[78%]`}>
                        {!isUser && (
                          <span className="text-[10px] font-semibold mb-1" style={{ color: "#7aa2ff" }}>
                            {t("Admin.support", lang)}
                          </span>
                        )}
                        <div className="rounded-2xl px-3.5 py-2.5"
                          style={{
                            background: isUser ? "rgba(139,92,246,0.15)" : "var(--surface-2)",
                            border: `1px solid ${isUser ? "rgba(139,92,246,0.25)" : "var(--border-color)"}`,
                            borderBottomRightRadius: isUser ? 6 : 16,
                            borderBottomLeftRadius:  isUser ? 16 : 6,
                          }}>
                          {msg.content && (
                            <div className="text-[13px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: "var(--text-primary)" }}>
                              <MsgText text={msg.content} lang={lang} onClose={() => setOpen(false)} />
                            </div>
                          )}
                          {msg.attachments?.length > 0 && (
                            <div className={`mt-1.5 grid gap-1 ${msg.attachments.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                              {msg.attachments.map((att, j) => (
                                <button key={j} type="button" onClick={() => setLbImg(resolveUrl(att.url))}
                                  className="group relative block overflow-hidden rounded-xl cursor-zoom-in"
                                  style={{ border: "1px solid var(--border-color)" }}>
                                  <img src={resolveUrl(att.url)} alt="" loading="lazy"
                                    className="max-h-32 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] mt-0.5 px-0.5" style={{ color: "var(--text-secondary)" }}>
                          {formatIsraelTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Closed banner + rating */}
                {isClosed && (
                  <>
                    <div className="flex items-center gap-2 my-3 py-2.5 px-3 rounded-2xl"
                      style={{ background: "rgba(74,222,128,0.07)", border: "1px solid rgba(74,222,128,0.15)" }}>
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                        {t("c.SupportWidget.this_conversation_was_closed", lang)}
                      </span>
                    </div>
                    {!activeTicket?.rating && (
                      <div className="flex flex-col items-center gap-2 py-3 px-4 rounded-2xl mb-2"
                        style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                        <p className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>
                          {t("c.SupportWidget.how_was_our_support", lang)}
                        </p>
                        <div className="flex gap-4">
                          {["up","down"].map(v => (
                            <button key={v} onClick={async () => {
                              await fetch(`${API}/tickets/${activeTicket.ticket_id}/rate`, {
                                method: "POST", credentials: "include",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ rating: v }),
                              });
                              fetchTickets();
                            }} className="text-2xl transition-transform hover:scale-125 active:scale-90">
                              {v === "up" ? "👍" : "👎"}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {activeTicket?.rating && (
                      <div className="flex items-center justify-center gap-1.5 py-2 px-4 rounded-2xl text-xs mb-2"
                        style={{ background: "var(--surface-1)", color: "var(--text-secondary)" }}>
                        {activeTicket.rating === "up" ? "👍" : "👎"}
                        {t("c.SupportWidget.thanks_for_the_feedback", lang)}
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} className="h-1" />
              </div>
            )}

            {/* ── Input area ───────────────────────────────────────────────────── */}
            {!inBotSelect && (
              <div className="flex-shrink-0" style={{ borderTop: "1px solid var(--border-color)", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 2px)" }}>

                {isClosed ? (
                  <div className="px-4 py-4 flex flex-col items-center gap-2.5">
                    <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {t("c.SupportWidget.need_anything_else_we_re_here", lang)}
                    </p>
                    <button onClick={startNewConversation}
                      className="h-10 px-5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                      style={{ background: "#8B5CF6" }}>
                      {t("c.SupportWidget.start_new_chat", lang)}
                    </button>
                  </div>

                ) : !activeId && !wantsHuman ? (
                  /* AI input */
                  <div className="px-4 py-3 flex flex-col gap-2.5">
                    {aiMessages.length === 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {AI_CHIPS.map((chip, i) => (
                          <button key={i} onClick={() => sendAiMessage(chip[lang] || chip.en)}
                            className="text-xs px-3 py-1.5 rounded-full font-medium transition-opacity active:scale-95 hover:opacity-80"
                            style={{ background: "rgba(139,92,246,0.1)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.2)" }}>
                            {chip[lang] || chip.en}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                        <Sparkles className="w-3.5 h-3.5 text-white" />
                      </div>
                      <input ref={aiInputRef}
                        value={aiInput}
                        onChange={e => setAiInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAiMessage(); } }}
                        placeholder={t("c.SupportWidget.ask_anything", lang)}
                        className="flex-1 h-10 rounded-xl px-3.5 text-sm focus:outline-none"
                        style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)", fontSize: "16px" }}
                        disabled={aiLoading}
                      />
                      <button onClick={() => sendAiMessage()} disabled={!aiInput.trim() || aiLoading}
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all active:scale-90 disabled:opacity-30"
                        style={{ background: aiInput.trim() ? "#8B5CF6" : "var(--surface-2)" }}>
                        {aiLoading
                          ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Send className="w-3.5 h-3.5 text-white" />}
                      </button>
                    </div>
                    <button onClick={() => { newConvMode.current = true; setWantsHuman(true); setBotTopic(null); setBotDetail(null); setBotDone(false); }}
                      className="text-xs text-center py-0.5 hover:underline"
                      style={{ color: "var(--text-secondary)" }}>
                      {t("c.SupportWidget.skip_ai_talk_to_a_real_person", lang)}
                    </button>
                  </div>

                ) : (
                  /* Reply box (human ticket) */
                  <>
                    {!activeId && botTopic && (
                      <div className="px-4 pt-2.5 flex items-center gap-1.5 flex-wrap">
                        {(() => {
                          const t = BOT_TOPICS.find(x => x.id === botTopic);
                          return t ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                              style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>
                              <t.Icon className="w-2.5 h-2.5" />
                              {t.label[lang] || t.label.en}
                            </span>
                          ) : null;
                        })()}
                        {botDetail && BOT_DETAILS[botTopic] && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                            {BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.emoji}{" "}
                            {BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.label[lang] || BOT_DETAILS[botTopic]?.opts.find(o => o.id === botDetail)?.label.en}
                          </span>
                        )}
                        {botLinkedId && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}>
                            {botLinkedType === "order" ? "📦" : "💳"} …{botLinkedId.slice(-6)}
                          </span>
                        )}
                        <button onClick={() => { setBotTopic(null); setBotDetail(null); setBotDone(false); setBotLinkedId(null); setBotLinkedType(null); setBotLinkStep(false); }}
                          className="text-[10px] opacity-50 hover:opacity-80" style={{ color: "var(--text-secondary)" }}>✕</button>
                      </div>
                    )}
                    {replyAtts.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 px-4 pt-2">
                        {replyAtts.map((a, i) => (
                          <div key={i} className="relative w-11 h-11 rounded-xl overflow-hidden group"
                            style={{ border: "1px solid var(--border-color)" }}>
                            <img src={resolveUrl(a.url)} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => setReplyAtts(c => c.filter((_, j) => j !== i))}
                              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ background: "rgba(0,0,0,0.5)" }}>
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2 px-3 py-2.5">
                      <label className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer hover:bg-white/5 transition-colors flex-shrink-0"
                        style={{ color: "var(--text-secondary)" }}>
                        <ImagePlus className="w-4 h-4" />
                        <input type="file" accept="image/*" multiple className="hidden" onChange={e => uploadFiles(e.target.files)} />
                      </label>
                      <textarea ref={textareaRef}
                        value={replyText}
                        onChange={e => {
                          setReplyText(e.target.value);
                          e.target.style.height = "auto";
                          e.target.style.height = Math.min(e.target.scrollHeight, 110) + "px";
                        }}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                        placeholder={t("c.SupportWidget.describe_your_issue", lang)}
                        rows={1}
                        className="flex-1 rounded-xl px-3.5 py-2 resize-none leading-relaxed focus:outline-none"
                        style={{
                          background: "var(--surface-2)",
                          border: `1px solid ${(wantsHuman && !activeId) ? "rgba(34,197,94,0.4)" : "var(--border-color)"}`,
                          color: "var(--text-primary)", minHeight: "40px", maxHeight: "110px", fontSize: "16px",
                        }}
                      />
                      <button onClick={sendReply}
                        disabled={sending || (!replyText.trim() && replyAtts.length === 0)}
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all active:scale-90 disabled:opacity-30"
                        style={{ background: (replyText.trim() || replyAtts.length) ? (wantsHuman && !activeId ? "#16a34a" : "#8B5CF6") : "var(--surface-2)" }}>
                        {sending
                          ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Send className="w-3.5 h-3.5 text-white" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════════ HISTORY VIEW ══════════════════════════════════════ */}
        {view === "history" && (
          <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1.5">

            {tickets.length > 0 && (
              <p className="text-[10px] font-bold uppercase tracking-wider px-1 py-1"
                style={{ color: "var(--text-secondary)" }}>
                {t("c.SupportWidget.past_conversations", lang)} ({tickets.length})
              </p>
            )}

            {tickets.map(ticket => {
              const unread   = ticket.user_unread_count || 0;
              const isActive = activeId === ticket.ticket_id;
              const lastMsg  = ticket.messages?.[ticket.messages.length - 1];
              const preview  = lastMsg?.content?.slice(0, 60) || (lastMsg?.attachments?.length ? "📎 Photo" : "");
              const lastSender = lastMsg?.sender_type === "user" ? t("c.SupportWidget.you", lang) + ": " : "";
              return (
                <button key={ticket.ticket_id} onClick={() => openHistoryTicket(ticket)}
                  className="w-full text-start rounded-2xl px-3.5 py-3 flex items-start gap-3 transition-all hover:opacity-90"
                  style={{
                    background: isActive ? "rgba(139,92,246,0.1)" : "var(--surface-1)",
                    border: `1px solid ${isActive ? "rgba(139,92,246,0.25)" : "var(--border-color)"}`,
                  }}>
                  <div className="relative flex-shrink-0 mt-0.5">
                    <div className="w-9 h-9 rounded-2xl flex items-center justify-center"
                      style={{ background: "var(--surface-2)" }}>
                      <MessageCircle className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                    </div>
                    <span className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 rounded-full"
                      style={{ background: sc(ticket.status), border: "2px solid var(--bg-secondary)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <span className="text-xs truncate"
                        style={{ color: unread ? "var(--text-primary)" : "var(--text-secondary)", fontWeight: unread ? 700 : 500 }}>
                        {ticket.subject}
                      </span>
                      <span className="text-[10px] flex-shrink-0 ms-1" style={{ color: "var(--text-secondary)" }}>
                        {relTime(ticket.updated_at, lang)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: sb(ticket.status), color: sc(ticket.status) }}>
                        {sl(ticket.status, lang)}
                      </span>
                      {preview && (
                        <p className="text-[11px] truncate flex-1" style={{ color: "var(--text-secondary)" }}>
                          {lastSender}{preview}
                        </p>
                      )}
                    </div>
                  </div>
                  {unread > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black text-white flex items-center justify-center flex-shrink-0"
                      style={{ background: "#8B5CF6" }}>
                      {unread}
                    </span>
                  )}
                </button>
              );
            })}

            {tickets.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-1"
                  style={{ background: "var(--surface-2)" }}>
                  <Clock className="w-6 h-6" style={{ color: "var(--text-secondary)" }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                  {t("c.SupportWidget.no_conversations_yet", lang)}
                </p>
                <p className="text-xs text-center" style={{ color: "var(--text-secondary)" }}>
                  {t("c.SupportWidget.your_past_chats_with_support_will_appear_here", lang)}
                </p>
                <button onClick={() => setView("chat")}
                  className="mt-2 h-9 px-4 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "#8B5CF6" }}>
                  {t("c.SupportWidget.start_a_conversation", lang)}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
